// Custom hooks for billing data fetching using React Query
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

// Hook to fetch current billing usage
export const useBillingUsage = (options = {}) => {
  return useQuery({
    queryKey: ['billingUsage'],
    queryFn: () => apiClient.getBillingUsage(),
    staleTime: 1000 * 60, // 1 minute
    ...options,
  });
};

// Hook to fetch billing usage history
export const useBillingUsageHistory = (months = 12, options = {}) => {
  return useQuery({
    queryKey: ['billingUsageHistory', months],
    queryFn: () => apiClient.getBillingUsageHistory(months),
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// Hook to fetch billing tiers
export const useBillingTiers = (options = {}) => {
  return useQuery({
    queryKey: ['billingTiers'],
    queryFn: () => apiClient.getBillingTiers(),
    staleTime: 1000 * 60 * 10, // 10 minutes (tiers don't change often)
    ...options,
  });
};

// Hook to check Stripe status
export const useStripeStatus = (options = {}) => {
  return useQuery({
    queryKey: ['stripeStatus'],
    queryFn: () => apiClient.getStripeStatus(),
    staleTime: 1000 * 60 * 10, // 10 minutes
    ...options,
  });
};

// Hook to create checkout session
export const useCreateCheckout = () => {
  return useMutation({
    mutationFn: ({ targetTier, successUrl, cancelUrl }) =>
      apiClient.createCheckoutSession(targetTier, successUrl, cancelUrl),
  });
};

// Hook to create portal session
export const useCreatePortal = () => {
  return useMutation({
    mutationFn: ({ returnUrl }) => apiClient.createPortalSession(returnUrl),
  });
};

// Hook to get credits balance
export const useCreditsBalance = (options = {}) => {
  return useQuery({
    queryKey: ['creditsBalance'],
    queryFn: () => apiClient.getCreditsBalance(),
    staleTime: 1000 * 30, // 30 seconds — balance changes with usage
    ...options,
  });
};

// Hook to create credits checkout
export const useCreateCreditsCheckout = () => {
  return useMutation({
    mutationFn: ({ amountUsd, successUrl, cancelUrl }) =>
      apiClient.createCreditsCheckout(amountUsd, successUrl, cancelUrl),
  });
};

// Analytics summary hook
export const useAnalyticsSummary = (months = 6, options = {}) => {
  return useQuery({
    queryKey: ['analyticsSummary', months],
    queryFn: () => apiClient.getAnalyticsSummary(months),
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: false,
    ...options,
  });
};
